import React, { useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    Chip,
    Tooltip,
    LinearProgress,
    TextField,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button
} from "@mui/material";
import {
    MoreVert as MoreVertIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Share as ShareIcon,
    Upload as UploadIcon,
    Download as DownloadIcon,
    FileCopy as FileCopyIcon,
    DriveFileRenameOutline as RenameIcon,
    AccountTree as OrganizeIcon,
    Info as InfoIcon,
    InfoOutlined as InfoOutlinedIcon,
    CheckCircle as CheckCircleIcon,
    Schedule as ScheduleIcon,
    AdsClick as AdsClickIcon,
    ExtensionSharp as ExtensionSharpIcon,
    Warning as WarningIcon,
    CalendarToday as CalendarTodayIcon
} from "@mui/icons-material";
import { Initiative } from "../types";
import { useTheme, SvgIcon, type SvgIconProps } from "@mui/material";

interface UpdatedProjectCardProps {
    project: Initiative;
    onProjectClick: (projectId: string) => void;
    onInfoClick: (project: Initiative, event: React.MouseEvent) => void;
    onUploadIconClick: (project: Initiative, event: React.MouseEvent) => void;
    onRename: (projectId: string) => void;
    onMakeCopy: (projectId: string) => void;
    onDownload: (projectId: string) => void;
    onShare: (projectId: string) => void;
    onOrganize: (projectId: string) => void;
    onMoveToTrash: (projectId: string) => void;
    onDeleteProject?: (projectId: string) => void;
    onEditProduct?: (projectId: string, event: React.MouseEvent) => void;
    editingCardId: string | null;
    editingTitle: string;
    onEditingTitleChange: (title: string) => void;
    onSaveRename: (projectId: string) => void;
    onCancelRename: () => void;
    isSelected?: boolean; // Optional selected state prop
}
const CustomFeatureIcon: React.FC<SvgIconProps> = props => {
    return (
        <SvgIcon {...props} viewBox="0 0 24 24">
            <path d="M12 22q-2.075 0-3.9-.788t-3.175-2.137T2.788 15.9T2 12t.788-3.9t2.137-3.175T8.1 2.788T12 2t3.9.788t3.175 2.137T21.213 8.1T22 12t-.788 3.9t-2.137 3.175t-3.175 2.138T12 22m0-2q3.35 0 5.675-2.325T20 12t-2.325-5.675T12 4T6.325 6.325T4 12t2.325 5.675T12 20m0-2q-2.5 0-4.25-1.75T6 12t1.75-4.25T12 6t4.25 1.75T18 12t-1.75 4.25T12 18m0-2q1.65 0 2.825-1.175T16 12t-1.175-2.825T12 8T9.175 9.175T8 12t1.175 2.825T12 16m0-2q-.825 0-1.412-.587T10 12t.588-1.412T12 10t1.413.588T14 12t-.587 1.413T12 14" />
        </SvgIcon>
    );
};

const UpdatedProjectCard: React.FC<UpdatedProjectCardProps> = ({
    project,
    onProjectClick,
    onInfoClick,
    onUploadIconClick,
    onRename,
    onMakeCopy,
    onDownload,
    onShare,
    onOrganize,
    onMoveToTrash,
    onDeleteProject,
    onEditProduct,
    editingCardId,
    editingTitle,
    onEditingTitleChange,
    onSaveRename,
    onCancelRename,
    isSelected = false
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const theme = useTheme();

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        console.log("Updated card menu clicked for project:", project.id);
        console.log("Button position:", event.currentTarget.getBoundingClientRect());
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleMenuItemClick = (action: () => void) => {
        action();
        handleMenuClose();
    };

    const handleDeleteClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (onDeleteProject) {
            try {
                await onDeleteProject(project.id);
                setDeleteDialogOpen(false);
            } catch (error) {
                console.error('Failed to delete project:', error);
                // Keep dialog open on error so user can try again
                alert('Failed to delete project. Please try again.');
            }
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
    };

    // Calculate counts
    const getUploadCounts = (project: Initiative) => {
        const projectUploads = project.uploads?.length || 0;
        const featureUploads = project.features.reduce((total, feature) => total + (feature.uploads?.length || 0), 0);
        return { projectUploads, featureUploads };
    };

    const getApprovedCount = (project: Initiative) => {
        // Only count approved work products that have been flagged by user interaction
        return project.features.reduce((total, feature) => {
            const approvedRequirements = feature.detailedRequirements?.filter(req => req.approved && req.userFlagged) || [];
            return total + approvedRequirements.length;
        }, 0);
    };

    const getInReviewCount = (project: Initiative) => {
        // Only count in-progress work products that have been opened by user
        return project.features.reduce((total, feature) => {
            const inReviewRequirements = feature.detailedRequirements?.filter(req => !req.approved && req.userOpened) || [];
            return total + inReviewRequirements.length;
        }, 0);
    };

    const { projectUploads, featureUploads } = getUploadCounts(project);
    const approvedCount = getApprovedCount(project);
    const inReviewCount = getInReviewCount(project);
    const totalUploads = projectUploads + featureUploads;
    const isEditing = editingCardId === project.id;

    // Helper function to get background color based on state
    const getBackgroundColor = () => {
        if (isSelected) return "#dbe5ff"; // Selected state - matches WorkProductCard active state
        return "#f7f9ff"; // Default state - matches WorkProductCard available-to-add state
    };

    // Helper function to format created date
    const formatCreatedDate = (dateValue: Date | string | undefined): string => {
        if (!dateValue) return "";
        const date = typeof dateValue === "string" ? new Date(dateValue) : dateValue;
        if (isNaN(date.getTime())) return "";
        return date.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric"
        });
    };

    return (
        <>
            <Card
                sx={{
                    width: "100%", // Fluid width that fills grid column
                    height: 280, // Fixed height
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                    bgcolor: getBackgroundColor(), // Dynamic background based on state
                    border: "1px solid",
                    borderColor: isSelected ? "#103699" : "divider", // Selected border matches primary color
                    transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)", // Match WorkProductCard transition
                    "&:hover": {
                        bgcolor: "#fafafa", // Hover fill - matches WorkProductCard hover state
                        elevation: 6,
                        boxShadow: theme => theme.shadows[6] // Match WorkProductCard hover shadow
                    },
                    "&:focus-visible": {
                        outline: "2px solid #103699", // Focus outline using primary color
                        outlineOffset: "2px",
                        bgcolor: "#fafafa" // Focus background matches hover
                    },
                    "&:active": {
                        bgcolor: "#edf2ff" // Active/pressed fill - matches WorkProductCard available state
                    }
                }}
                elevation={isSelected ? 2 : 1} // Higher elevation for selected cards
                onClick={() => !isEditing && onProjectClick(project.id)}
                tabIndex={0} // Make focusable for keyboard navigation
                role="button" // Better accessibility
                aria-pressed={isSelected} // Indicate selection state to screen readers
            >
                <CardContent
                    sx={{
                        flex: 1,
                        p: 2, // 16px padding inside card
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        overflow: "hidden" // Prevent content overflow
                    }}
                >
                    {/* Header Row with Context Menu */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2
                        }}
                    >
                        {/* Left side - Status and counts */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                            {/* Status Chip */}
                            {/* <Chip
                                label={project.status}
                                size="small"
                                color={
                                    project.status === "active"
                                        ? "success"
                                        : project.status === "planning"
                                        ? "warning"
                                        : project.status === "completed"
                                        ? "info"
                                        : "default"
                                }
                                variant="outlined"
                                sx={{ fontSize: "0.7rem", height: 24 }}
                            /> */}

                            {/* Approved Count */}
                            {/* <Tooltip title={`${approvedCount} Approved`}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
                                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                        {approvedCount}
                                    </Typography>
                                </Box>
                            </Tooltip> */}

                            {/* In Progress Count */}
                            {/* <Tooltip title={`${inReviewCount} In Progress`}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <ScheduleIcon sx={{ fontSize: 16, color: "warning.main" }} />
                                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                        {inReviewCount}
                                    </Typography>
                                </Box>
                            </Tooltip> */}
                        </Box>

                        {/* Right side - Action icons */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {/* Edit Product Icon */}
                            {onEditProduct && (
                                <Tooltip title="Edit Product">
                                    <IconButton
                                        size="small"
                                        onClick={e => {
                                            e.stopPropagation();
                                            onEditProduct(project.id, e);
                                        }}
                                        sx={{
                                            color: "text.secondary",
                                            "&:hover": {
                                                color: "primary.main",
                                                bgcolor: "action.hover"
                                            }
                                        }}
                                    >
                                        <EditIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                            )}

                            {/* Context Menu Icon */}
                            {/* <IconButton
                                size="small"
                                onClick={handleMenuClick}
                                sx={{
                                    color: "text.secondary",
                                    "&:hover": {
                                        bgcolor: "action.hover"
                                    }
                                }}
                            >
                                <MoreVertIcon fontSize="small" />
                            </IconButton> */}
                        </Box>
                    </Box>

                    {/* Title Row */}
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2, flex: 1 }}>
                        <AdsClickIcon sx={{ fontSize: 20, color: "primary.main", mt: 0.2, flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            {isEditing ? (
                                <TextField
                                    value={editingTitle}
                                    onChange={e => onEditingTitleChange(e.target.value)}
                                    onBlur={() => onSaveRename(project.id)}
                                    onKeyDown={e => {
                                        if (e.key === "Enter") {
                                            onSaveRename(project.id);
                                        } else if (e.key === "Escape") {
                                            onCancelRename();
                                        }
                                    }}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    autoFocus
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            fontSize: "1.125rem",
                                            fontWeight: 600
                                        }
                                    }}
                                />
                            ) : (
                                <>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "1.125rem",
                                            lineHeight: 1.3,
                                            overflow: "hidden",
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            wordBreak: "break-word",
                                            mb: 0.5
                                        }}
                                    >
                                        {project.name}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "text.secondary",
                                            fontSize: "0.875rem",
                                            lineHeight: 1.4,
                                            overflow: "hidden",
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            wordBreak: "break-word"
                                        }}
                                    >
                                        {project.description || "No description available"}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Box>

                    {/* Progress Bar */}
                    {/* <Box sx={{ mb: 2 }}>
                        <LinearProgress
                            variant="determinate"
                            value={project.progress}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: "action.hover",
                                "& .MuiLinearProgress-bar": {
                                    borderRadius: 4
                                }
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                            {project.progress}% Complete
                        </Typography>
                    </Box> */}

                    {/* Bottom Row - Created Date, Feature Count, and Delete */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                        {/* Left side - Created Date and Feature Count stacked */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            {/* Created Date */}
                            {project.startDate && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <CalendarTodayIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                        {formatCreatedDate(project.startDate)}
                                    </Typography>
                                </Box>
                            )}
                            {/* Feature Count */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <CustomFeatureIcon sx={{ fontSize: 20, color: theme.palette.primary.main, flexShrink: 0 }} />
                                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                    {project.features.length} Features
                                </Typography>
                            </Box>
                        </Box>

                        {/* Delete Icon - Bottom Right */}
                        <Tooltip title="Delete Product">
                            <IconButton
                                size="small"
                                onClick={handleDeleteClick}
                                sx={{
                                    color: "text.primary",
                                    "&:hover": {
                                        bgcolor: "action.hover"
                                    }
                                }}
                            >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </CardContent>
            </Card>

            {/* Context Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right"
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right"
                }}
            >
                <MenuItem onClick={() => handleMenuItemClick(() => onUploadIconClick(project, new MouseEvent("click") as any))}>
                    <UploadIcon sx={{ mr: 1, fontSize: 16 }} />
                    Upload
                </MenuItem>
                <MenuItem onClick={() => handleMenuItemClick(() => onDownload(project.id))}>
                    <DownloadIcon sx={{ mr: 1, fontSize: 16 }} />
                    Download
                </MenuItem>
                <MenuItem onClick={() => handleMenuItemClick(() => onRename(project.id))}>
                    <RenameIcon sx={{ mr: 1, fontSize: 16 }} />
                    Rename
                </MenuItem>
                <MenuItem onClick={() => handleMenuItemClick(() => onMakeCopy(project.id))}>
                    <FileCopyIcon sx={{ mr: 1, fontSize: 16 }} />
                    Make a copy
                </MenuItem>
                <MenuItem onClick={() => handleMenuItemClick(() => onShare(project.id))}>
                    <ShareIcon sx={{ mr: 1, fontSize: 16 }} />
                    Share
                </MenuItem>
                <MenuItem onClick={() => handleMenuItemClick(() => onOrganize(project.id))}>
                    <OrganizeIcon sx={{ mr: 1, fontSize: 16 }} />
                    Organize
                </MenuItem>
                <MenuItem onClick={() => handleMenuItemClick(() => onInfoClick(project, new MouseEvent("click") as any))}>
                    <InfoIcon sx={{ mr: 1, fontSize: 16 }} />
                    Project Overview
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => handleMenuItemClick(() => onMoveToTrash(project.id))} sx={{ color: "error.main" }}>
                    <DeleteIcon sx={{ mr: 1, fontSize: 16 }} />
                    Move to trash
                </MenuItem>
            </Menu>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        minWidth: 400
                    }
                }}
            >
                <DialogTitle id="delete-dialog-title" sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
                    <WarningIcon sx={{ color: "error.main", fontSize: 28 }} />
                    Delete Product
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description" sx={{ mb: 2 }}>
                        Are you sure you want to delete <strong>{project.name}</strong>?
                    </DialogContentText>
                    <DialogContentText sx={{ color: "error.main", fontWeight: 500 }}>This will permanently delete:</DialogContentText>
                    <Box component="ul" sx={{ mt: 1, pl: 2, color: "text.secondary" }}>
                        <li>The product and all its data</li>
                        <li>
                            All {project.features.length} feature{project.features.length !== 1 ? "s" : ""} in this product
                        </li>
                        <li>All user stories related to these features</li>
                        <li>All work products associated with this product</li>
                    </Box>
                    <DialogContentText sx={{ mt: 2, fontWeight: 600, color: "error.dark" }}>This action cannot be undone!</DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleDeleteCancel} variant="outlined" color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} variant="contained" color="error" autoFocus startIcon={<DeleteIcon />}>
                        Delete Product
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UpdatedProjectCard;
